<script setup lang="ts">
import ProgressSpinner from 'primevue/progressspinner';
import Card from 'primevue/card';
import Toolbar from 'primevue/toolbar';
import { createModel } from '@kp/js-model';
import { computed, onMounted, ref, type Ref } from 'vue';
import { useToast } from 'primevue/usetoast';
import { errToToast, reallyCancel } from '@/helpers/tools';
import { onBeforeRouteLeave, onBeforeRouteUpdate, useRouter } from 'vue-router';
import { getI18n } from '@/helpers/i18n';
import KpButton from '@/components/KpButton.vue';
import KpNumberField from '@/components/KpNumberField.vue';
import { Standardgemeinde } from '@/models/Standardgemeinde';
import { createValidator, createValidators, validateNotEmpty, validateNumberBetween } from '@/helpers/model_validators';
import KpTextField from '@/components/KpTextField.vue';
import KpGemeindeSelect from '@/components/KpGemeindeSelect.vue';

const props = defineProps<{
    // id is injected via router:
    id: number | 'new';
}>();

const router = useRouter();
const toast = useToast();
const record: Ref<Standardgemeinde | null> = ref(null);
const saving = ref(false);

const { allValid, validators } = createValidators({
    plz: createValidator(
        computed(() => record.value?.plz || null),
        [validateNotEmpty, validateNumberBetween(1000, 9999)],
    ),
    ort: createValidator(
        computed(() => record.value?.ort),
        [validateNotEmpty],
    ),
    gemeinde: createValidator(
        computed(() => record.value?.gemeinde_id),
        [validateNotEmpty],
    ),
});

onMounted(() => {
    loadRecord(props.id);
});

onBeforeRouteLeave(() => {
    return reallyCancel(record?.value);
});

onBeforeRouteUpdate((to, from) => {
    if (to.params.id !== from.params.id) {
        loadRecord(to.params.id === 'new' ? 'new' : Number(to.params.id));
    }
});

async function loadRecord(id: number | 'new') {
    if (id === 'new') {
        record.value = createModel(Standardgemeinde);
    } else {
        try {
            const rec = await Standardgemeinde.loadById(id);
            if (rec) {
                record.value = rec;
            } else {
                record.value = null;
                throw new Error(getI18n().t('record_not_found'));
            }
        } catch (e) {
            errToToast(e, toast);
        }
    }
}

async function onSave() {
    if (record?.value === null) {
        return;
    }
    saving.value = true;
    try {
        const isNew = !Number(record.value.id);
        await record.value.save();
        record.value.commit();
        toast.add({
            severity: 'success',
            detail: getI18n().t('datensatz_gespeichert'),
            life: 1000,
        });
        if (isNew) {
            router.push({ name: 'bg_prod_standardgemeinde_edit', params: { id: record.value.id } });
        }
    } catch (e) {
        errToToast(e, toast);
    } finally {
        saving.value = false;
    }
}

function onCancel() {
    if (reallyCancel(record?.value)) {
        record?.value?.rollback();
        router.push({ name: 'bg_prod_standardgemeinde' });
    }
}
</script>
<template>
    <div v-if="!record" class="flex justify-content-center">
        <ProgressSpinner></ProgressSpinner>
    </div>
    <div v-else>
        <Card class="kp-max-content-width">
            <template #header>
                <Toolbar class="toolbar">
                    <template #start>
                        <h2>
                            <i class="fas fa-user"></i> {{ $t('standardgemeinden') }}:
                            {{ record.id || $t('new') }}
                        </h2>
                    </template>
                    <template #end>
                        <div class="flex gap-1">
                            <KpButton
                                severity="success"
                                icon="far fa-floppy-disk"
                                v-tooltip="$t('speichern')"
                                :loading="saving"
                                :disabled="!allValid"
                                @click="onSave"
                            />
                            <KpButton
                                severity="secondary"
                                icon="far fa-xmark"
                                v-tooltip="$t('abbrechen')"
                                :disabled="saving"
                                @click="onCancel"
                            />
                        </div>
                    </template>
                </Toolbar>
            </template>
            <template #content>
                <div class="formgrid grid align-items-start">
                    <KpNumberField
                        v-model="record.plz"
                        :label="$t('plz')"
                        class="col-6"
                        fluid
                        :use-grouping="false"
                        :invalid="!validators.plz.isValid.value"
                        :error="validators.plz.msg.value"
                    ></KpNumberField>
                    <KpTextField
                        v-model="record.ort"
                        :label="$t('ort')"
                        class="col-6"
                        fluid
                        :invalid="!validators.ort.isValid.value"
                        :error="validators.ort.msg.value"
                    ></KpTextField>
                </div>
                <div class="formgrid grid align-items-start">
                    <KpGemeindeSelect
                        v-model="record.gemeinde_id"
                        :label="$t('gemeinde')"
                        mandant
                        class="col-6"
                        fluid
                        :invalid="!validators.gemeinde.isValid.value"
                        :error="validators.gemeinde.msg.value"
                    ></KpGemeindeSelect>
                    <div class="col-6" fluid></div>
                </div>
            </template>
        </Card>
    </div>
</template>

<style lang="css" scoped>
.toolbar {
    border: none;
    border-bottom: 1px solid var(--p-toolbar-border-color);
    h2 {
        margin: 0;
    }
}
</style>
